import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowTopRightOnSquareIcon, AcademicCapIcon } from '@heroicons/react/24/solid';

// Data for the page
const roadmapSteps = [
    { id: 1, title: 'Core Concepts', description: "Understand the 'Why' behind Quality Assurance and its role in the SDLC.", link: { to: '/about', text: 'Learn about QA' } },
    { id: 2, title: 'Manual Testing', description: 'Master the art of test cases, bug reporting, and exploratory testing.', link: { to: '/tutorials', text: 'Go to Tutorials' } },
    { id: 3, title: 'Essential Tools', description: 'Get to know industry standards like JIRA and TestRail for management.', link: { to: '/tools', text: 'Explore Tools' } },
    { id: 4, title: 'Automation Basics', description: 'Begin your journey with Selenium or Cypress and a scripting language.', link: { to: '/automation-learning', text: 'Start Automation' } },
    { id: 5, title: 'API Testing', description: 'Learn to test the backbone of modern applications using tools like Postman.', link: { to: '/tools', text: 'See API Tools' } },
    { id: 6, title: 'Build & Practice', description: 'Apply your knowledge, test your skills, and prepare for your first QA role.', link: { to: '/quizzes', text: 'Test Your Knowledge' } }
];

const certifications = [
    { name: 'ISTQB Certified Tester Foundation Level (CTFL)', description: 'The most widely recognized certification for software testers globally.', link: 'https://www.istqb.org/' },
    { name: 'Udemy/Coursera QA Courses', description: 'Platforms that offer high-quality, project-based courses that provide certificates.', link: 'https://www.udemy.com/topic/qa-software-testing/' },
    { name: 'Certified Associate in Software Testing (CAST)', description: 'Offered by the Quality Assurance Institute (QAI), a well-respected certification.', link: 'https://www.softwarecertifications.org/' }
];

// --- Animation Variants ---
const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.3 } },
};

const stepVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
};

const cardHover = {
    y: -8,
    boxShadow: "0px 20px 30px rgba(0,0,0,0.1)",
    transition: { type: 'spring', stiffness: 300 },
};

// Main Component
const VisualRoadmapPage = () => {
    return (
        <div className="space-y-24">
            {/* Page Header */}
            <motion.div
                className="text-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                    Visual QA Engineer Roadmap
                </h1>
                <p className="mt-4 max-w-3xl mx-auto text-xl text-gray-600">
                    A graphical journey illustrating the key milestones to becoming a successful QA professional.
                </p>
            </motion.div>

            {/* NEW Visual Roadmap "Subway Map" Section */}
            <section className="w-full">
                {/* Desktop Subway Map */}
                <div className="hidden lg:block">
                    <motion.div
                        className="relative flex justify-between items-center w-full"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.1 }}
                    >
                        {/* The main track line */}
                        <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-200" />

                        {roadmapSteps.map((step, index) => (
                            <motion.div key={step.id} className="relative z-10 flex flex-col items-center" variants={stepVariants}>
                                {/* Step card on top */}
                                {index % 2 !== 0 && <RoadmapNode step={step} />}

                                {/* Connector and Station */}
                                <div className={`w-1 ${index % 2 !== 0 ? 'h-12 bg-gray-300' : 'h-12 bg-transparent'}`} />
                                <div className="w-5 h-5 rounded-full bg-indigo-600 ring-4 ring-white" />
                                <div className={`w-1 ${index % 2 === 0 ? 'h-12 bg-gray-300' : 'h-12 bg-transparent'}`} />

                                {/* Step card at bottom */}
                                {index % 2 === 0 && <RoadmapNode step={step} />}
                            </motion.div>
                        ))}
                    </motion.div>
                </div>

                {/* Fallback for mobile: A simpler, stacked list */}
                <div className="relative max-w-xl mx-auto lg:hidden space-y-8">
                    {roadmapSteps.map((step) => <RoadmapNode key={step.id} step={step} isMobile={true} />)}
                </div>
            </section>

            {/* Certifications Section (Reused) */}
            <motion.section
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.3 }}
            >
                <div className="text-center">
                    <h2 className="text-3xl font-bold text-gray-900">Recommended Certifications</h2>
                    <p className="mt-3 max-w-2xl mx-auto text-lg text-gray-600">
                        Certifications can validate your skills and make your resume stand out.
                    </p>
                </div>
                <motion.div
                    className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    {certifications.map(cert => (
                        <motion.div
                            key={cert.name}
                            className="bg-white p-6 rounded-xl shadow-lg h-full flex flex-col"
                            variants={{ hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1 } }}
                            whileHover={cardHover}
                        >
                            <div className="flex items-start">
                                <div className="flex-shrink-0"> <AcademicCapIcon className="h-8 w-8 text-indigo-500" /></div>
                                <div className="ml-4">
                                    <h3 className="text-lg font-semibold text-gray-900">{cert.name}</h3>
                                    <p className="mt-2 text-base text-gray-600">{cert.description}</p>
                                </div>
                            </div>
                            <div className="mt-auto pt-4">
                                <a href={cert.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center font-semibold text-indigo-600 hover:text-indigo-500">
                                    Visit Website <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4" />
                                </a>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.section>
        </div>
    );
};


// Helper component for the roadmap nodes
const RoadmapNode = ({ step, isMobile = false }) => (
    <motion.div
        className={`bg-white p-6 rounded-lg shadow-xl border border-gray-100 text-center ${isMobile ? 'w-full' : 'w-64'}`}
        whileHover={!isMobile ? cardHover : {}}
    >
        <h3 className="text-xl font-bold text-indigo-600">{step.title}</h3>
        <p className="mt-2 text-gray-600 text-sm">{step.description}</p>
        <Link to={step.link.to} className="mt-4 inline-block font-semibold text-sm text-indigo-600 hover:underline">
            {step.link.text} &rarr;
        </Link>
    </motion.div>
);


export default VisualRoadmapPage;